import { UniversityProfileQuery } from "@packages/lib/graphQL/graphql-query";
import { graphQlFetchFunction } from "@packages/lib/server-actions/server-action";



export const domainMap: Record<string, string> = {
  dev: "https://mdev.dev.aws.whatuni.com",
  stg: "https://mtest.test.aws.whatuni.com",
  prd: "https://www.whatuni.com",
};




export async function fetchUniversityProfile(skip?: number): Promise<any> {
  const query = UniversityProfileQuery(skip);

  const result = await graphQlFetchFunction(query);

  if (!result.data) {
    throw new Error("fetchUniversityProfile: no data returned");
  }

  return result.data;
}

export function getStudentFeedback(sectionsObj: any, title: any) {
  const wanted = title.trim().toLowerCase();

  const entry = Object.entries(sectionsObj).find(([key, val]: any) => {
    const name = val.sectionName?.trim().toLowerCase() || "";
    const display = val.displayName?.trim().toLowerCase() || "";
    return name === wanted || display === wanted || key.toLowerCase() === title;
  });

  const section = entry?.[1] || null;
  return section;
}

export function mergeTemplate<T extends Record<string, any>>(
  template: T[],
  stats: Record<string, number | string>
): T[] {
  return template.map((item) => {
    const merged = { ...item } as any;

    // 1) Overview‐style: single `value` prop, pick stats by matching label → key
    if ("value" in item && "title" in item) {
      const normLabel = String(item.title).toLowerCase().replace(/\s+/g, "");
      const statKey = (Object.keys(stats) as (keyof typeof stats)[]).find((k) =>
        k.toLowerCase().includes(normLabel)
      );
      merged.value = statKey ? stats[statKey] : merged.value;
      return merged;
    }

    for (const key of Object.keys(item)) {
      if (
        key.endsWith("_title") ||
        key === "title" ||
        key === "description" ||
        key === "icon"
      ) {
        continue;
      }
      if (key in stats) {
        merged[key] = stats[key as keyof typeof stats];
      }
      if (key === "internationalStudents" && "international" in stats) {
        merged[key] = stats.international;
      }
    }

    return merged;
  });
}

export const getOrdinalSuffix = (n: number): string => {
  const j = n % 10,
    k = n % 100;
  if (j === 1 && k !== 11) return "st";
  if (j === 2 && k !== 12) return "nd";
  if (j === 3 && k !== 13) return "rd";
  return "th";
};

export function addMediaType(mediaObject: {
  mediaId: number;
  mediaPath: string;
  thumbnailImage: string;
}) {
  if (!mediaObject || typeof mediaObject.mediaPath !== "string") {
    console.warn(
      "Invalid input: mediaObject or mediaPath is missing or not a string."
    );
    return { ...mediaObject, mediaType: null };
  }

  const mediaPath = mediaObject.mediaPath;
  let mediaType = null;

  const lastDotIndex = mediaPath.lastIndexOf(".");
  if (lastDotIndex > -1) {
    const extension = mediaPath.substring(lastDotIndex + 1).toLowerCase();

    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
    const videoExtensions = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"];

    if (imageExtensions.includes(extension)) {
      mediaType = "image";
    } else if (videoExtensions.includes(extension)) {
      mediaType = "video";
    }
  }

  return { ...mediaObject, mediaType: mediaType };
}


export function formatValueWithCommas(dataArray:any, targetId: any) {
  return dataArray?.map((item: any) => {
      if (item.id === targetId) {
          const numericValue = parseInt(item.value, 10);

          if (!isNaN(numericValue)) {
              return { ...item, value: numericValue.toLocaleString() };
          }
      }
      return item;
  });
}

export const formatRating = (rating: any) => {
  if (Number.isInteger(rating) && rating >= 0 && rating <= 9) {
    return rating.toFixed(1);
  }
  return rating;
};


export const generateBreadcrumbSchema = (breadcrumbs: any[], baseUrl: string) => {
  const itemList = breadcrumbs.map((item, index) => {
    const url = item.url?.startsWith("http")
      ? item.url
      : `${baseUrl.replace(/\/$/, "")}${item.url || ""}`;

    const listItem: any = {
      "@type": "ListItem",
      position: index + 1,
      name: item.label || "Home",
    };

    if (item.url) {
      listItem.item = url;
    }

    return listItem;
  });

  return itemList;
};
